import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
  TablePagination,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Dashboard } from "./Dashboard";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import { Wrapper } from "../../components";
import { apiAnalyticsGrouped, apiAnalyticsGroupedExport } from "../../services";


const LogsSkeletonRows = ({ rows = 10 }) => (
  <>
    {Array.from({ length: rows }).map((_, index) => (
      <TableRow key={index}>
        <TableCell><Skeleton width={120} /></TableCell>
        <TableCell><Skeleton width={80} /></TableCell>
        <TableCell><Skeleton width={60} /></TableCell>
        <TableCell><Skeleton width={60} /></TableCell>
        <TableCell><Skeleton width={60} /></TableCell>
        <TableCell><Skeleton width={80} /></TableCell>
        <TableCell><Skeleton width={60} /></TableCell>
      </TableRow>
    ))}
  </>
);

const getStatus = (row) => {
  if (Number(row.successRate) === 100) return "Success";
  if (Number(row.failureCount) > 0 && Number(row.successCount) === 0) return "Failed";
  return "Partial";
};

const getStatusColor = (status) => {
  if (status === "Success") return "#16A34A";
  if (status === "Failed") return "#d32f2f";
  return "#F97316";
};

const CONTROL_HEIGHT = 38;

const FilterDropdown = ({ label, selectLabel, options, selected, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const toggle = (value) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FilterAltOutlinedIcon sx={{ fontSize: 18 }} />}
        endIcon={open ? <KeyboardArrowUpIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          height: CONTROL_HEIGHT,
          textTransform: "none",
          borderRadius: 3,
          px: 2,
          fontSize: "14px",
          color: "#0f172a",
          borderColor: selected.length > 0 ? "#2563EB" : "#e2e8f0",
          backgroundColor: "#fff",
        }}
      >
        {label}
        {selected.length > 0 && (
          <Chip
            label={selected.length}
            size="small"
            sx={{ ml: 0.5, height: 20, fontSize: "11px", fontWeight: 700, bgcolor: "#EFF6FF", color: "#2563EB" }}
          />
        )}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: 3, mt: 1, minWidth: 220, p: 1 } }}
      >
        <Typography sx={{ px: 1, pt: 0.5, pb: 1, fontSize: "14px", fontWeight: 600 }}>
          {selectLabel}
        </Typography>
        {options.map((opt) => (
          <Box
            key={opt}
            onClick={() => toggle(opt)}
            sx={{
              display: "flex",
              alignItems: "center",
              px: 1,
              py: 0.5,
              cursor: "pointer",
              borderRadius: 1,
              "&:hover": { bgcolor: "#F8FAFC" },
            }}
          >
            <Checkbox
              checked={selected.includes(opt)}
              size="small"
              sx={{ p: 0, mr: 1 }}
            />
            <Typography sx={{ fontSize: "14px" }}>{String(opt)}</Typography>
          </Box>
        ))}
      </Popover>
    </>
  );
};

export function ApiLogs() {
  const navigate = useNavigate();
  const { companyName } = useParams();
  const decodedCompanyName = companyName ? decodeURIComponent(companyName) : null;
  const { user } = useSelector((state) => state.auth);
  const jwtToken = user?.jwt;

  const currentDate = dayjs();
  const sevenDaysBack = currentDate.subtract(7, "day");

  const [dashboardDates, setDashboardDates] = useState({
    startDate: sevenDaysBack.startOf("day").format("YYYY-MM-DD"),
    endDate: currentDate.endOf("day").format("YYYY-MM-DD"),
  });

  const [logs, setLogs] = useState([]);
  const [totalLogsCount, setTotalLogsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedApiTypes, setSelectedApiTypes] = useState([]);
  const [selectedStatusCodes, setSelectedStatusCodes] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [allApiNames, setAllApiNames] = useState([]);

  useEffect(() => {
    if (!jwtToken || !decodedCompanyName) return;

    const fetchLogs = async () => {
      try {
        setLoading(true);
        if (selectedApiTypes.length <= 1) {
          const res = await apiAnalyticsGrouped(jwtToken, decodedCompanyName, dashboardDates.startDate, dashboardDates.endDate, selectedApiTypes[0] || null, page, rowsPerPage);
          if (res?.status === 200) {
            setLogs(res.data?.logs || res.data?.data || res.data || []);
            setTotalLogsCount(res.data?.pagination?.totalRecords || res.data?.pagination?.total || res.data?.total || 0);
          }
        } else {
          const results = await Promise.all(
            selectedApiTypes.map((name) =>
              apiAnalyticsGrouped(jwtToken, decodedCompanyName, dashboardDates.startDate, dashboardDates.endDate, name, 1, 1000)
            )
          );
          const allLogs = results.flatMap((res) => {
            if (res?.status === 200) return res.data?.logs || res.data?.data || res.data || [];
            return [];
          });
          setTotalLogsCount(allLogs.length);
          setLogs(allLogs.slice((page - 1) * rowsPerPage, page * rowsPerPage));
        }
      } catch (error) {
        console.error("Failed to fetch API logs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [jwtToken, decodedCompanyName, dashboardDates, selectedApiTypes, page, rowsPerPage]);

  useEffect(() => {
    if (!jwtToken || !decodedCompanyName) return;
    apiAnalyticsGrouped(jwtToken, decodedCompanyName, dashboardDates.startDate, dashboardDates.endDate, null, 1, 1000)
      .then((res) => {
        if (res?.status === 200) {
          const data = res.data?.logs || res.data?.data || res.data || [];
          setAllApiNames([...new Set(data.map((l) => l.apiName).filter(Boolean))]);
        }
      })
      .catch(() => {});
  }, [jwtToken, decodedCompanyName, dashboardDates]);

  const apiTypeOptions = allApiNames;

  const filteredLogs = logs;

  useEffect(() => {
    setPage(1);
  }, [selectedApiTypes, selectedStatusCodes, dashboardDates]);

  const handleDownloadLogs = async () => {
    try {
      const res = await apiAnalyticsGroupedExport(jwtToken, decodedCompanyName, dashboardDates.startDate, dashboardDates.endDate);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${decodedCompanyName}_api_logs.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download logs:", error);
    }
  };

  return (
    <Wrapper>
      <Stack sx={{ pt: 2, mt: 7 }} spacing={2} className="wrapper">
        {/* <Box
          onClick={() => navigate("/customer-analytics")}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.4,
            width: "fit-content",
            borderRadius: 3,
            border: "1px solid #E2E8F0",
            fontSize: "13px",
            fontWeight: 500,
            color: "#475569",
            backgroundColor: "#FFFFFF",
            cursor: "pointer",
            whiteSpace: "nowrap",
            "&:hover": { bgcolor: "#F8FAFC", color: "#020817" },
          }}
        >
          <ArrowBackIcon fontSize="small" />
          Back to All Customers
        </Box> */}
        <Dashboard companyName={decodedCompanyName} onDatesChange={setDashboardDates} />
        <Paper sx={{ width: "100%", borderRadius: 3, boxShadow: 2, p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                API Logs
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "1.4rem", fontWeight: 400 }}
              >
                Usage logs for {decodedCompanyName}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <FilterDropdown
                label="API Type"
                selectLabel="Select API Types"
                options={apiTypeOptions}
                selected={selectedApiTypes}
                onChange={setSelectedApiTypes}
              />
              {/* <FilterDropdown
                label="Status Code"
                selectLabel="Select Status Codes"
                options={statusOptions}
                selected={selectedStatusCodes}
                onChange={setSelectedStatusCodes}
              /> */}
              <Button
                variant="contained"
                onClick={handleDownloadLogs}
                sx={{
                  height: CONTROL_HEIGHT,
                  textTransform: "none",
                  borderRadius: 3,
                  px: 3,
                  fontSize: "14px",
                  fontWeight: 600,
                  bgcolor: "#2563EB",
                  color: "#fff",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#1D4ED8", boxShadow: "none" },
                }}
              >
                Download logs
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {["API Name", "Total Requests", "Success Count", "Failed Count", "Success Rate", "Credits Utilized"].map(
                    (header) => (
                      <TableCell
                        key={header}
                        sx={{ fontSize: "14px", fontWeight: 600, color: "#64748B", textAlign: "center" }}
                      >
                        {header}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <LogsSkeletonRows />
                ) : filteredLogs.length > 0 ? (
                  filteredLogs.map((row, index) => {
                    const status = getStatus(row);
                    return (
                      <TableRow key={index}>
                        <TableCell
                          sx={{ fontSize: "14px", textAlign: "center", color: "#2563EB", fontWeight: 500, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                          onClick={() => navigate(`/dashboard/${encodeURIComponent(companyName)}/view-logs?apiName=${encodeURIComponent(row.apiName)}&startDate=${dashboardDates.startDate}&endDate=${dashboardDates.endDate}`)}
                        >
                          {row.apiName}
                        </TableCell>
                        <TableCell sx={{ fontSize: "14px", textAlign: "center" }}>{row.totalRequests}</TableCell>
                        <TableCell sx={{ fontSize: "14px", textAlign: "center" }}>{row.successCount}</TableCell>
                        <TableCell sx={{ fontSize: "14px", textAlign: "center" }}>{row.failureCount}</TableCell>
                        <TableCell sx={{ fontSize: "14px", textAlign: "center" }}>{row.successRate}%</TableCell>
                        {/* <TableCell sx={{ fontSize: "14px", textAlign: "center", color: getStatusColor(status), fontWeight: 500 }}>{status}</TableCell> */}
                        <TableCell sx={{ fontSize: "14px", textAlign: "center" }}>{row.creditsUtilized}</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, fontSize: "15px" }}>
                      No logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalLogsCount}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setPage(1);
              setRowsPerPage(Number(e.target.value));
            }}
            rowsPerPageOptions={[15, 25, 50, 100]}
            sx={{
              mt: 2,
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "12px",
                fontWeight: 600,
              },
              "& .MuiTablePagination-select": {
                fontSize: "12px",
              },
            }}
          />
        </Paper>
      </Stack>
    </Wrapper>
  );
}
